import React, { useEffect, useState, useCallback } from 'react';
import { Table, Tag, Button, Space, Modal, Form, Input, Select, Popconfirm, message, Typography, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined } from '@ant-design/icons';
import { useFetchWrapper } from '_helpers';
import { useCapabilities } from '_helpers/capabilities';

const { Text } = Typography;
const { Option } = Select;

export { StaffManagePage };

// Staff roles this page manages, with display label + tag colour.
const ROLE_META = {
    teacher:    { label: 'Giáo viên',  color: 'blue'   },
    manager:    { label: 'Quản lý',    color: 'gold'   },
    headmaster: { label: 'Hiệu trưởng', color: 'purple' },
};

function StaffManagePage() {
    const fetchWrapper = useFetchWrapper();
    const { canEditStudentId, canResetPasswords } = useCapabilities();   // full-manager / manager+headmaster
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving]   = useState(false);
    const [modal, setModal]     = useState(null);     // null | {mode:'create'} | {mode:'edit', record}
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res  = await fetchWrapper.get('/api/manager/staff', null, null);
            const json = await res.json();
            if (json.status === 'Success') setRows(Array.isArray(json.message) ? json.message : []);
            else message.error(json.message || 'Không tải được danh sách nhân sự');
        } catch (e) {
            message.error('Không tải được danh sách nhân sự');
        }
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    function openCreate() {
        form.resetFields();
        form.setFieldsValue({ role: 'teacher' });
        setModal({ mode: 'create' });
    }

    function openEdit(record) {
        form.setFieldsValue({
            name:         record.name,
            role:         record.role,
            email:        record.email || '',
            phone_number: record.phone_number || '',
            student_id:   record.student_id,
        });
        setModal({ mode: 'edit', record });
    }

    async function handleSave() {
        let values;
        try { values = await form.validateFields(); } catch { return; }
        setSaving(true);
        try {
            const body = new URLSearchParams();
            body.append('name',         values.name);
            body.append('role',         values.role);
            body.append('email',        values.email || '');
            body.append('phone_number', values.phone_number || '');
            // student_id only meaningful for full managers; server ignores it otherwise.
            if (canEditStudentId && values.student_id) body.append('student_id', values.student_id.trim());

            const isEdit = modal.mode === 'edit';
            const url = isEdit ? `/api/manager/staff/${modal.record._id}` : '/api/manager/staff';
            const res  = await (isEdit ? fetchWrapper.put : fetchWrapper.post)(
                url, 'application/x-www-form-urlencoded', body
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success(isEdit
                    ? 'Đã cập nhật'
                    : `Đã tạo "${values.name}" — mật khẩu mặc định: themason`);
                setModal(null);
                await load();
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Có lỗi xảy ra');
            }
        } catch (e) {
            message.error('Có lỗi xảy ra');
        }
        setSaving(false);
    }

    async function handleReset(record) {
        try {
            const res  = await fetchWrapper.post(`/api/manager/users/${record._id}/reset-password`, null, null);
            const json = await res.json();
            if (json.status === 'Success') message.success(`Đã đặt lại mật khẩu: ${json.message.default}`);
            else message.error(typeof json.message === 'string' ? json.message : 'Đặt lại thất bại');
        } catch (e) {
            message.error('Đặt lại thất bại');
        }
    }

    async function handleDelete(record) {
        try {
            const res  = await fetchWrapper.delete(`/api/manager/staff/${record._id}`, null, null);
            const json = await res.json();
            if (json.status === 'Success') { message.success('Đã xóa'); await load(); }
            else message.error(typeof json.message === 'string' ? json.message : 'Xóa thất bại');
        } catch (e) {
            message.error('Xóa thất bại');
        }
    }

    const columns = [
        {
            title: 'Tên',
            key: 'name',
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{r.name || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{r.student_id}</div>
                </div>
            ),
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            title: 'Vai trò',
            key: 'role',
            width: 130,
            filters: Object.entries(ROLE_META).map(([v, m]) => ({ text: m.label, value: v })),
            onFilter: (v, r) => r.role === v,
            render: (_, r) => {
                const m = ROLE_META[r.role] || { label: r.role, color: 'default' };
                return <Tag color={m.color}>{m.label}</Tag>;
            },
        },
        { title: 'Email', dataIndex: 'email', key: 'email', render: v => v || <Text type="secondary">—</Text> },
        { title: 'SĐT', dataIndex: 'phone_number', key: 'phone_number', width: 130, render: v => v || <Text type="secondary">—</Text> },
        {
            title: '',
            key: 'actions',
            width: 100,
            render: (_, r) => (
                <Space size={4}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                    {canResetPasswords && (
                        <Popconfirm
                            title={`Đặt lại mật khẩu của "${r.name}" về "themason"?`}
                            onConfirm={() => handleReset(r)}
                            okText="Đặt lại" cancelText="Hủy"
                        >
                            <Button size="small" icon={<KeyOutlined />} title="Đặt lại mật khẩu" />
                        </Popconfirm>
                    )}
                    <Popconfirm
                        title={`Xóa "${r.name}"?`}
                        onConfirm={() => handleDelete(r)}
                        okText="Xóa" cancelText="Hủy"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="att2-page soft-in">
            <div className="att2-head">
                <div className="att2-head-l">
                    <h1 className="att2-title">Quản lý giáo viên</h1>
                    <div className="att2-sub">{rows.length} nhân sự</div>
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Thêm nhân sự</Button>
            </div>

            <div className="float-panel soft-in-scale">
                <Table
                    columns={columns}
                    dataSource={rows.map(r => ({ ...r, key: r._id }))}
                    loading={loading}
                    pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                    locale={{ emptyText: <Empty description="Chưa có nhân sự" /> }}
                />
            </div>

            <Modal
                title={modal?.mode === 'edit' ? 'Chỉnh sửa nhân sự' : 'Thêm nhân sự'}
                visible={!!modal}
                onCancel={() => setModal(null)}
                onOk={handleSave}
                confirmLoading={saving}
                okText={modal?.mode === 'edit' ? 'Lưu' : 'Tạo'}
                cancelText="Hủy"
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Họ và tên" rules={[{ required: true, message: 'Nhập tên' }]}>
                        <Input placeholder="Nguyễn Văn A" />
                    </Form.Item>
                    <Form.Item name="role" label="Vai trò" rules={[{ required: true }]}>
                        <Select>
                            {Object.entries(ROLE_META).map(([v, m]) => <Option key={v} value={v}>{m.label}</Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item name="email" label="Email">
                        <Input type="email" placeholder="email@themason.uk" />
                    </Form.Item>
                    <Form.Item name="phone_number" label="Số điện thoại">
                        <Input placeholder="09..." />
                    </Form.Item>
                    {canEditStudentId && (
                        <Form.Item
                            name="student_id"
                            label="Mã đăng nhập"
                            tooltip="Để trống khi tạo mới → hệ thống tự sinh (mason...). Đây cũng là tên đăng nhập."
                        >
                            <Input placeholder="Tự sinh nếu để trống" />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </div>
    );
}
